<script setup lang="ts">
import LineCheckpoint from "@/components/LineCheckpoint.vue";
import MPDImage from "@/components/MPDImage.vue";
import SphereBlock from "@/components/SphereBlock.vue";
import SectionContactWithRows from "@/components/sections/SectionContactWithRows.vue";
import LineDrawing from "@/components/LineDrawing.vue";
import FrameServiceSphere from "@/components/frames/FrameServiceSphere.vue";
import ServiceSphereSection from "@/components/ServiceSphereSection.vue";
import Button from "@/components/Button.vue";
import ServiceSphereSectionContent from "@/components/ServiceSphereSectionContent.vue";
import Framer from "@/components/io/atomic/atoms/Framer.vue";

import ATitle from "@/components/io/atomic/atoms/fragments/content/ATitle.vue";

useSeoMeta({
  title: 'Tailored Hygiene Programs for Hospitality | Altracare',
  description: 'Explore Altracare’s hygiene programs blending equipment, dosing, training and scheduled support to raise standards across Zanzibar hotels, kitchens and facilities.',
  ogTitle: 'Tailored Hygiene Programs for Hospitality | Altracare',
  ogDescription: 'Explore Altracare’s hygiene programs blending equipment, dosing, training and scheduled support to raise standards across Zanzibar hotels, kitchens and facilities.',
  ogType: 'website',
  twitterCard: 'summary_large_image'
})

const { scrollToId } = useScrollTo()
const router = useRouter()

// Define the array of valid strings for this page
const validStrings = ['assessment', 'solutions', 'implementation', 'support', 'improvement']

const route = useRoute()

const { hero } = useHero()

onMounted(() => {
  hero({
    key: 'our-service',
    autoHeight: true,
    autoHeightSettings: {
      minHeight: '80vh',
      maxHeight: '80vh',
    },
    base: {
      class: 'our-service',
      height: '80vh',
      minHeight: '80vh',
      maxHeight: '80vh',
    },

    media: {
      type: 'image',
      transition: {
        mode: 'out-in'
      },
      media: {
        src: '/images/industries/energy-and-biofuel.jpg',
        alt: '360 degree service',
      },
    },
    content: {
      model: "page",
      transition: {
        mode: 'out-in'
      },
      content: {
        component: "",
        hairline: 'Altracare — More Than a Supplier',
        hairlineColour: 'white',
        title: 'Raising Hygiene Standards Through <span class="sky-blue">Tailored 360°</span> Service.',
        titleColour: 'white',
        description: 'From a complimentary assessment to continuous improvement, we partner with you at every step to deliver spotless results, staff confidence and assured compliance.',
        descriptionColour: 'white',
        line: true,
        lineColor: 'var(--c-cyan)',
      }
    },
    overlays: {
      transition: {
        mode: 'out-in'
      },
      overlays: [{
        type: 'gradient',
        options: {
          direction: 'to bottom',
          colors: ['rgba(128, 0, 128, 0.2)', 'rgba(0, 0, 255, 0.2)'],
        },
      }]
    },
  })
})

const routeQuery = computed(() => route.query)

function processRouteQueryScroll() {  // Loop through each valid string and check if it exists as a query parameter
  for (const param of validStrings) {
    const keys = Object.keys(route.query)
    console.log("param", param)
    console.log("keys", keys)
    // If the query parameter is present and matches a valid string, scroll to the corresponding element
    if (keys.includes(param)) {
      setTimeout(() => {
        nextTick(() => {
          scrollToId(param, -80)
        })
      }, 500)

      break // Exit loop after the first match to avoid multiple scrolls
    }
  }
}

watch(routeQuery, () => {
  processRouteQueryScroll()
}, { immediate: true })

onMounted(() => {
  hero({
    media: {
      type: 'image',
      transition: {
        mode: 'out-in'
      },
      media: {
        src: '/images/industries/energy-and-biofuel.jpg',
        alt: '360 degree service',
      },
    },
    content: {
      model: "page",
      transition: {
        mode: 'out-in'
      },
      content: {
        hairline: '360 degree excellence ',
        title: 'We understand the importance of getting it right.',
        description: 'From a complimentary assessment to continuous improvement, we partner with you at every step to deliver spotless results, staff confidence and assured compliance.',
        line: true,
        lineColor: 'var(--c-cyan)',
      }
    },
    overlays: {
      transition: { mode: 'out-in' },
      overlays: [{
        type: 'gradient',
        styles: {
          kind: 'linear',
          direction: 180, // top -> bottom
          stops: [
            { color: 'rgba(0,0,0,0.00)', at: '45%' },
            { color: 'rgba(0,0,0,0.75)', at: '80%' },
            { color: 'rgba(0,0,0,1.00)', at: '100%' }
          ],
          blendMode: 'normal'
        },
        breakpoints: [
          {
            from: 1024, until: -1,
            styles: {
              stops: [
                { color: 'rgba(0,0,0,0.00)', at: '40%' },
                { color: 'rgba(0,0,0,0.85)', at: '78%' },
                { color: 'rgba(0,0,0,1.00)', at: '100%' }
              ]
            }
          }
        ]
      }, {
        type: 'gradient',
        styles: {
          kind: 'linear',
          direction: 'to right',
          stops: [
            { color: 'rgba(0,40,140,0.45)', at: '0%' },
            { color: 'rgba(0,40,140,0.20)', at: '35%' },
            { color: 'rgba(0,40,140,0.00)', at: '70%' }
          ],
          blendMode: 'soft-light'
        },
        breakpoints: [
          {
            from: 1024, until: -1,
            styles: {
              direction: 'to bottom right',
              stops: [
                { color: 'rgba(0,40,140,0.50)', at: '0%' },
                { color: 'rgba(0,40,140,0.00)', at: '60%' }
              ],
              blendMode: 'multiply'
            }
          }
        ]
      }]
    },

  })
})

const checkpoints = ref<Checkpoint[]>([
  {
    id: "introsection",
    angle: 45,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },
  {
    id: "chkassessment",
    angle: 80,
    gradient: {
      from: "#cbd5e1", //
      to: "#cbd5e1"
    }
  },
  {
    id: "chksolution",
    angle: 80,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },
  {
    id: "chkimplementation",
    angle: 80,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },
  {
    id: "chksupport",
    angle: 80,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },
  {
    id: "chkimprovement",
    angle: 80,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },
  {
    id: "chkcontact",
    angle: 80,
    gradient: {
      from: "#cbd5e1",
      to: "#cbd5e1"
    }
  },

])


// Only initialize useElementSize when framer element is available
const framer = ref(null)
const { width: framerWidth, height: framerHeight } = useElementSize(framer)


const isMounted = useMounted()


</script>

<template>
  <Section class="page page-our-service" w="mw-100 mx-auto">

    <Framer ref="framer" class="frame-primary" w="grid mw-100 mx-auto g-3">

      <div class="sec-background-lines">
        <LineDrawing v-if="isMounted" :checkpoints="checkpoints" :width="framerWidth" :height="framerHeight" />
      </div>

      <Section class="sec-primary-content relative z-1 gpt-0">

        <Section v-if="false" class="sec-360-intro-section gmb-2"
          w="grid grid-cols-[1fr] mlx:Lmax-w-85% mx-auto ds:grid-cols-[0.8fr_1fr] g-1.5 ds:g-4 d:g-2 gpt-0">
          <Frame class="sec-intro-left" w="grid g-1 max-w-85% mx-auto">

            <ATitle class="a-title" hairline="Welcome to Altracare"
              primary="The 360<span class='deg'>°</span> Altracare Service"
              description="At the intersection of advanced technology and deep-rooted passion, Altracare offers an unparalleled experience. While many tread the path of Digital Solutions, few match our holistic approach. Here’s what makes us distinct:">
            </ATitle>

            <Frame class="a-actions">
              <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                @click="scrollToId('assessment')">
                Our Services
              </Button>
            </Frame>
          </Frame>
          <Frame class="sec-service-items" w="grid g-1 max-w-85% mx-auto">
            <LineCheckpoint id="introsection" />
            <div class="sec-service-items-container">
              <button @click="scrollToId('assessment', -80)" class="sec-service-item one items-start! gpy-1!">
                <div class="sec-index gpt-0">
                  <div class="outer">
                    <div class="inner">
                      01
                    </div>
                  </div>
                </div>

                <div class="sec-content">
                  <div class="sec-title fs-1!">
                    Assessment & Discovery
                  </div>
                  <div class="sec-description">
                    Laying the right foundations through site insights and consultation.
                  </div>
                </div>
              </button>
              <button @click="scrollToId('solution', -80)" class="sec-service-item two items-start! gpy-1!">
                <div class="sec-index gpt-0">
                  <div class="outer">
                    <div class="inner">
                      02
                    </div>
                  </div>
                </div>

                <div class="sec-content">
                  <div class="sec-title fs-1!">
                    Tailored Solutions
                  </div>
                  <div class="sec-description">
                    Integrated hygiene plans designed for quality, safety and efficiency.
                  </div>
                </div>
              </button>
              <button @click="scrollToId('implementation', -80)" class="sec-service-item three items-start! gpy-1!">
                <div class="sec-index gpt-0">
                  <div class="outer">
                    <div class="inner">
                      03
                    </div>
                  </div>
                </div>

                <div class="sec-content">
                  <div class="sec-title fs-1!">
                    Implementation & Training
                  </div>
                  <div class="sec-description">
                    Seamless delivery, setup and hands-on training for your team.
                  </div>
                </div>
              </button>
              <button @click="scrollToId('support', -80)" class="sec-service-item four items-start! gpy-1!">
                <div class="sec-index gpt-0">
                  <div class="outer">
                    <div class="inner">
                      04
                    </div>
                  </div>
                </div>

                <div class="sec-content">
                  <div class="sec-title fs-1!">
                    Ongoing Support
                  </div>
                  <div class="sec-description">
                    Regular technical visits, audits and compliance monitoring.
                  </div>
                </div>
              </button>
              <button @click="scrollToId('improvement', -80)" class="sec-service-item five items-start! gpy-1!">
                <div class="sec-index gpt-0">
                  <div class="outer">
                    <div class="inner">
                      05
                    </div>
                  </div>
                </div>

                <div class="sec-content">
                  <div class="sec-title fs-1!">
                    Continuous Improvement
                  </div>
                  <div class="sec-description">
                    Reviews, innovation and refinements that keep you ahead.
                  </div>
                </div>
              </button>
            </div>
          </Frame>
        </Section>

        <div class="sec-sphere-sections grid g-3 gmb-2 ds:gmb-3">
          <ServiceSphereSection id="assessment" secti onTitle="01. Assessment & Discovery" dir="ltr">
            <template #media>
              <MPDImage image="/images/kitchen-4.jpg" />
            </template>
            <template #content>
              <ServiceSphereSectionContent id="assessment" w="grid g-1">

                <ATitle class="sphere-content-title">
                  <template #hairline>
                    <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                      Quality, safety and efficiency—designed together.
                    </div>
                  </template>
                  <template #primary>
                    <div class="a-title-primary fs-4! glh-2! font-secondary">
                      We design integrated hygiene plans that balance <span
                        class="text-[var(--cyan-500)]!">performance</span>, <span
                        class="text-[var(--green-500)] !important">eco-friendliness</span> and <span
                        class="text-[var(--green-500)] !important">cost.</span>
                    </div>
                  </template>
                </ATitle>

                <Section class="columns-1" w="grid g-1">
                  <p>Drawing on world-class suppliers and local insight, we specify the right chemistry, dosing systems
                    and procedures
                    for each area—kitchen, laundry, housekeeping and front-of-house.</p>
                  <p>The outcome is consistency you can rely on and standards that are simple for teams to maintain.</p>

                  <Frame>
                    <div class="sphere-list grid g-0.5">
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Right-product, right-dose specifications for every zone</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Eco-conscious options to reduce waste and water</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Clear SOPs that are easy for teams to follow</span>
                      </div>
                    </div>
                  </Frame>
                </Section>

                <Section class="a-actions">
                  <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                    @click="scrollToId('contact', -80)">
                    Contact us
                  </Button>
                </Section>

              </ServiceSphereSectionContent>
            </template>

            <!-- <template #blocks>
              <SphereBlock title="Precision Curation"
                description="We select products and systems proven in hospitality environments like yours." />
              <SphereBlock title="Tailored Fusion"
                description="Chemistry, equipment and process are tuned to your exact throughput." />
              <SphereBlock title="Compliance by Design"
                description="Safety data, labelling and signage are built into the plan." />
            </template> -->
          </ServiceSphereSection>

          <ServiceSphereSection id="solution" sectionTitle="02. Tailored Solutions" dir="rtl">
            <template #media>
              <MPDImage class="sticky! top-100px!" image="/chem-7.jpg" />
            </template>
            <template #content>
              <ServiceSphereSectionContent id="solution" w="grid g-1">

                <ATitle class="a-title sphere-content-title">
                  <template #hairline>
                    <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                      Quality, safety and efficiency—designed together.
                    </div>
                  </template>
                  <template #primary>
                    <div class="a-title-primary fs-4! glh-2! font-secondary">
                      We design integrated hygiene plans that balance <span
                        class="text-[var(--cyan-500)]!">performance</span>, <span
                        class="text-[var(--green-500)] !important">eco-friendliness</span> and <span
                        class="text-[var(--green-500)] !important">cost.</span>
                    </div>
                  </template>
                </ATitle>

                <Section class="columns-1" w="grid g-1">
                  <p>Drawing on world-class suppliers and local insight, we specify the right chemistry, dosing systems,
                    and
                    procedures for each area—kitchen, laundry, housekeeping, and front-of-house.</p>

                  <p>The outcome is consistency you can rely on and standards that are simple for teams to maintain.</p>

                  <Frame>
                    <div class="sphere-list grid g-0.5">
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Right-product, right-dose specifications for every zone</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Eco-conscious options to reduce waste and water</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Clear SOPs that are easy for teams to follow</span>
                      </div>
                    </div>
                  </Frame>
                </Section>

                <Section class="a-actions">
                  <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                    @click="scrollToId('contact', -80)">
                    Contact us
                  </Button>
                </Section>

              </ServiceSphereSectionContent>
            </template>

            <template #blocks>
              <SphereBlock title="Precision Curation"
                description="We select products and systems proven in hospitality environments like yours." />
              <SphereBlock title="Tailored Fusion"
                description="Chemistry, equipment and process are tuned to your exact throughput." />
              <SphereBlock title="Compliance by Design"
                description="Safety data, labelling and signage are built into the plan." />
            </template>
          </ServiceSphereSection>

          <ServiceSphereSection id="implementation" sectionTitle="03. Implementation & Training" dir="ltr">
            <template #media>
              <MPDImage image="/images/page/our-service/bottles.jpg" />
            </template>
            <template #content>

              <ServiceSphereSectionContent id="implementation" w="grid g-1">
                <ATitle class="a-title sphere-content-title">
                  <template #hairline>
                    <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                      From plan to practice—without disruption.
                    </div>
                  </template>
                  <template #primary>
                    <div class="a-title-primary fs-4! glh-2! font-secondary">
                      We deliver, install and train so your team can <span class="text-[var(--blue-500)]!">achieve the standard from day one. </span>
                    </div>
                  </template>
                </ATitle>

                <Section class="columns-1" w="grid g-1">
                  <p>At Altracare, our implementation process ensures precision and efficiency at every stage. Technicians install and calibrate dosing and dispensers, while trainers coach your staff on safe, efficient use. </p>

                  <p>We leave you with simple checklists and visual cues so quality remains consistent—shift after shift. Our approach exemplifies the reliability and expertise of Altracare, providing a smooth, worry-free experience from start to finish.</p>

                  <Frame>
                    <div class="sphere-list grid g-0.5">
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Professional installation and calibration of dispensers/equipment</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Hands-on staff training with demos and safety basics</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Practical job-aids: wall charts, labels and quick guides</span>
                      </div>
                    </div>
                  </Frame>

                </Section>

                <Section class="a-actions">
                  <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                    @click="scrollToId('contact', -80)">
                    Contact us
                  </Button>
                </Section>
              </ServiceSphereSectionContent>
            </template>

            <template #blocks>
              <SphereBlock title="Seamless Setup"
                description="Hardware, stock and signage deployed with minimal downtime." />
              <SphereBlock title="Team Enablement" description="Brief, practical sessions that stick with staff." />
              <SphereBlock title="First-Week Assurance"
                description="Early follow-up to verify results and close gaps." />
            </template>

          </ServiceSphereSection>

          <ServiceSphereSection id="support" sectionTitle="04. Ongoing Support" dir="rtl">
            <template #media>
              <MPDImage image="/images/cleaning-team.jpg" />
            </template>
            <template #content>
              <ServiceSphereSectionContent id="support" w="grid g-1">

                <ATitle class="a-title sphere-content-title">
                  <template #hairline>
                    <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                      Proactive care that keeps you inspection-ready.
                    </div>
                  </template>
                  <template #primary>
                    <div class="a-title-primary fs-4! glh-2! font-secondary">
                      Regular visits, audits and rapid response <span class="text-[var(--cyan-500)]!">keep your
                        standards high</span> and your systems running.
                    </div>
                  </template>
                </ATitle>

                <Section class="columns-1" w="grid g-1">
                  <p>At Altracare, our Support & Waste Management services reflect our commitment to both safety and
                    sustainability. Our technicians conduct scheduled checks on dispensers and inventories, and our audits confirm procedures are working. You get clear reports, updated signage, and refresher coaching where needed.</p>
                  <Frame>
                    <div class="sphere-list grid g-0.5">
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Planned Maintenance — Dispenser checks, stock control and calibration.</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Compliance in View — Signage, SDS and documentation always current.</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>On-Call Help — Fast support when something breaks or changes.</span>
                      </div>
                    </div>
                  </Frame>

                </Section>

                <Section class="a-actions">
                  <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                    @click="scrollToId('contact', -80)">
                    Contact us
                  </Button>
                </Section>

              </ServiceSphereSectionContent>
            </template>

            <template #blocks>
              <SphereBlock title="Planned Maintenance" description="Dispenser checks, stock control and calibration." />
              <SphereBlock title="Compliance in View" description="Signage, SDS and documentation always current." />
              <SphereBlock title="On-Call Help" description="Fast support when something breaks or changes." />
            </template>
          </ServiceSphereSection>

          <ServiceSphereSection id="improvement" sectionTitle="05. Continuous Improvement" dir="ltr">
            <template #media>
              <MPDImage image="/images/refinement.jpg" />
            </template>
            <template #content>

              <ServiceSphereSectionContent id="improvement" w="grid g-1">
                <ATitle class="a-title sphere-content-title">
                  <template #hairline>
                    <div class="a-title-hairline glh-1! text-[var(--heading-hairline)]">
                      Standards rise when systems evolve.
                    </div>
                  </template>
                  <template #primary>
                    <div class="a-title-primary fs-4! glh-2! font-secondary">
                      <span class="text-[var(--cyan-500)]!">We analyse results</span> and roll in new ideas so your
                      operation keeps getting better.
                    </div>
                  </template>
                </ATitle>

                <Section class="columns-1" w="grid g-1">
                  <p>Using audit data and feedback, we refine products, dosages, and routines to save time, reduce waste, and elevate the guest experience. You benefit from ongoing innovation—new formulations, better tools, and smarter processes.
                  </p>

                  <Frame>
                    <div class="sphere-list grid g-0.5">
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Review & Reveal — We turn data into simple decisions that lift outcomes.</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Refine & Prove — Small changes, measured impact, sustained gains.</span>
                      </div>
                      <div class="list-item flex items-center g-0.5 text-[var(--body-primary)]">
                        <i class="flex w-4 h-4 i-mdi:check text-green-500" />
                        <span>Innovate & Share — You get early access to improvements that matter.</span>
                      </div>
                    </div>
                  </Frame>

                </Section>

                <Section class="a-actions">
                  <Button btn="blue" class="gpy-0.75 gpx-1.5 fs--1 fw-600 flex items-center g-0.5"
                    @click="scrollToId('contact', -80)">
                    Contact us
                  </Button>
                </Section>
              </ServiceSphereSectionContent>
            </template>

            <template #blocks>
              <SphereBlock title="Review & Reveal"
                description="We turn data into simple decisions that lift outcomes." />
              <SphereBlock title="Refine & Prove" description="Small changes, measured impact, sustained gains." />
              <SphereBlock title="Innovate & Share" description="You get early access to improvements that matter." />
            </template>


          </ServiceSphereSection>

        </div>

        <SectionContactWithRows id="contactus" />

      </Section>


    </Framer>

  </Section>
</template>



<style lang="scss">
.io-hero-content {
  &.our-service {


    .content-container {
      grid-template-columns: 1fr !important;
    }

    .col-content {
      gap: gr(1.5) !important;

      @include mq($from: mlx) {
        max-width: 85% !important;
      }

      &.glass-container {

        .glass-background {
          backdrop-filter: blur(8px) !important;

          @include mq($until: t) {
            display: none;
          }
        }
      }

      .io-h-hairline {}

      .io-h-primary-title {
        font-weight: 400;
      }

      .io-h-description {}
    }
  }
}
</style>

<style lang="scss" scoped>
.page-our-service {
  .bg-layer {
    background-image: url('/bg9.png');
    background-attachment: fixed;
    background-size: cover;
  }

}






.sec-360-intro-section {
  >.m-wrap {
    grid-template-columns: 1fr 1fr;

    >.m-col {

      >.m-wrap {
        max-width: 80%;
        margin: 0 auto;
      }

      &.sec-intro-left {
        >.m-wrap {

          display: grid;
          gap: gr(1);

          .sec-small {
            font-size: fs(1);
            line-height: gr(1);
            font-weight: 300;
            color: var(--blue-500);
          }

          .sec-primary {
            font-size: fs(7);
            line-height: gr(2.5);
            font-weight: 300;
            color: var(--heading-1);

          }

          .sec-description {
            color: var(--body-1)
          }

          .sec-actions {
            >.action {}
          }

        }
      }

      &.sec-service-items {
        .sec-service-items-container {
          display: grid;
          gap: gr(1);

          @include mq($until: ds) {
            gap: gr(0.75);
          }

          >.sec-service-item {
            display: flex;
            gap: gr(1.5);
            border-radius: gr(1);
            background: rgba(255, 255, 255, 0.85);
            backdrop-filter: blur(20px) saturate(180%);
            -webkit-backdrop-filter: blur(20px) saturate(180%);
            cursor: pointer;
            align-items: center;
            padding: gr(1.5);
            border: 0;
            position: relative;
            overflow: hidden;
            transition: all 0.3s ease;

            @include mq($until: t) {
              padding: gr(1.25);
              gap: gr(1.25);
            }

            // Modern glass border
            &::before {
              content: '';
              position: absolute;
              inset: 0;
              padding: 1.5px;
              background: linear-gradient(135deg,
                  rgba(255, 255, 255, 0.5) 0%,
                  rgba(255, 255, 255, 0.2) 50%,
                  rgba(255, 255, 255, 0.1) 100%);
              border-radius: inherit;
              mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
              mask-composite: subtract;
              -webkit-mask-composite: destination-out;
              opacity: 0.6;
              transition: opacity 0.3s ease;
            }

            // Gradient overlay for depth
            &::after {
              content: '';
              position: absolute;
              inset: 0;
              background: linear-gradient(to bottom right,
                  transparent 0%,
                  rgba(59, 130, 246, 0.02) 50%,
                  rgba(147, 51, 234, 0.02) 100%);
              border-radius: inherit;
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.3s ease;
            }

            // Modern shadow with blur
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06),
            0 2px 12px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(0, 0, 0, 0.02);

            * {
              cursor: pointer;
            }

            >.sec-index {
              display: grid;
              place-items: center;
              flex-shrink: 0;
              transition: all 0.3s ease;
              position: relative;
              z-index: 1;

              >.outer {
                display: grid;
                place-items: center;
                width: gr(3.5);
                height: gr(3.5);
                border-radius: gr(0.75);
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                box-shadow:
                  0 1px 3px rgba(0, 0, 0, 0.05),
                  0 0 0 1px rgba(0, 0, 0, 0.03);
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;

                @include mq($until: t) {
                  width: gr(3);
                  height: gr(3);
                }

                // Inner shine effect
                &::before {
                  content: '';
                  position: absolute;
                  top: -50%;
                  left: -50%;
                  width: 200%;
                  height: 200%;
                  background: linear-gradient(45deg,
                      transparent 30%,
                      rgba(255, 255, 255, 0.3) 50%,
                      transparent 70%);
                  transform: rotate(45deg) translateX(-100%);
                  transition: transform 0.6s ease;
                }

                >.inner {
                  font-size: fs(1);
                  line-height: 1;
                  font-weight: 700;
                  color: #334155;
                  position: relative;
                  z-index: 1;
                  font-variant-numeric: tabular-nums;
                  letter-spacing: -0.02em;

                  @include mq($until: t) {
                    font-size: fs(0.75);
                  }
                }
              }
            }

            >.sec-content {
              display: grid;
              gap: gr(0.5);
              flex: 1;
              position: relative;
              z-index: 1;

              * {
                text-align: left;
              }

              >.sec-title {
                font-size: fs(1);
                line-height: gr(1.5);
                font-weight: 600;
                color: #0f172a;
                transition: all 0.3s ease;
                letter-spacing: -0.02em;

                @include mq($until: t) {
                  font-size: fs(0.75);
                  line-height: gr(1.25);
                }
              }

              >.sec-description {
                font-size: fs(0);
                line-height: gr(1.5);
                font-weight: 400;
                color: #64748b;
                transition: all 0.3s ease;

                @include mq($until: t) {
                  font-size: fs(-0.25);
                  line-height: gr(1.25);
                }
              }
            }

            &:hover {
              transform: translateY(-2px);
              background: rgba(255, 255, 255, 0.95);
              box-shadow:
                0 12px 32px rgba(0, 0, 0, 0.08),
                0 4px 16px rgba(0, 0, 0, 0.05),
                0 0 0 1px rgba(0, 0, 0, 0.03);

              &::before {
                opacity: 1;
              }

              &::after {
                opacity: 1;
              }

              >.sec-index {
                >.outer {
                  transform: scale(1.05);
                  background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
                  box-shadow:
                    0 4px 12px rgba(0, 0, 0, 0.08),
                    0 0 0 1px rgba(0, 0, 0, 0.04);

                  &::before {
                    transform: rotate(45deg) translateX(0);
                  }

                  >.inner {
                    color: #1e293b;
                  }
                }
              }

              >.sec-content {
                >.sec-title {
                  color: #0f172a;
                  transform: translateX(4px);
                }

                >.sec-description {
                  color: #475569;
                  transform: translateX(4px);
                }
              }

              &:active {
                transform: translateY(0);
                box-shadow:
                  0 4px 12px rgba(0, 0, 0, 0.06),
                  0 2px 8px rgba(0, 0, 0, 0.04),
                  0 0 0 1px rgba(0, 0, 0, 0.02);
              }
            }

            // Modern accent colors with subtle gradients
            &.one {
              &::after {
                background: linear-gradient(to bottom right,
                    transparent 0%,
                    rgba(59, 130, 246, 0.03) 50%,
                    rgba(29, 78, 216, 0.03) 100%);
              }

              &:hover>.sec-index>.outer {
                background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);

                >.inner {
                  color: #1e40af;
                }
              }
            }

            &.two {
              &::after {
                background: linear-gradient(to bottom right,
                    transparent 0%,
                    rgba(6, 182, 212, 0.03) 50%,
                    rgba(8, 145, 178, 0.03) 100%);
              }

              &:hover>.sec-index>.outer {
                background: linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%);

                >.inner {
                  color: #0e7490;
                }
              }
            }

            &.three {
              &::after {
                background: linear-gradient(to bottom right,
                    transparent 0%,
                    rgba(16, 185, 129, 0.03) 50%,
                    rgba(5, 150, 105, 0.03) 100%);
              }

              &:hover>.sec-index>.outer {
                background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);

                >.inner {
                  color: #047857;
                }
              }
            }

            &.four {
              &::after {
                background: linear-gradient(to bottom right,
                    transparent 0%,
                    rgba(245, 158, 11, 0.03) 50%,
                    rgba(217, 119, 6, 0.03) 100%);
              }

              &:hover>.sec-index>.outer {
                background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);

                >.inner {
                  color: #b45309;
                }
              }
            }

            &.five {
              &::after {
                background: linear-gradient(to bottom right,
                    transparent 0%,
                    rgba(139, 92, 246, 0.03) 50%,
                    rgba(124, 58, 237, 0.03) 100%);
              }

              &:hover>.sec-index>.outer {
                background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);

                >.inner {
                  color: #6d28d9;
                }
              }
            }
          }
        }
      }
    }
  }
}

.sec-services-scroll-container-new {
  :deep(>.m-wrap) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding: 2rem;

    .timeline {
      position: relative;
      width: 3px;
      background: #ccc;
    }
  }

}

.sec-blocks {
  .sec-block {
    .sec-icon {

      left: 0;

      .icon {
        .outer {
          width: gr(2);
          height: gr(2);
        }
      }
    }
  }
}


.sec-contact-form {

  position: relative;

  padding-top: gr(5);

  @include mq($until: t) {
    padding-top: gr(10);
  }

  :deep(.a-title) {
    @include mq($until: t) {
      padding-top: gr(2);
    }
  }

  :deep(.a-checkpoint) {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-100%, 0%);

    @include mq($until: t) {
      transform: translate(25%, -125%);
    }
  }
}


.col-media {}


.sec-sphere-section {

  .sec-service-section {
    .sec-header {

      .sec-checkpoint {}

      .sec-title {

        .sec-index {
          .outer {
            font-size: fs(3);
            font-weight: 300;
            color: var(--base-500);
          }
        }

        .sec-title-text {
          line-height: gr(1);
          font-weight: 300;
          color: var(--base-500);
          font-size: fs(3);
        }
      }
    }

    .row-content {
      .col-media {}

      .col-sphere-content {
        .a-title {
          :deep(.a-title-primary) {
            font-size: fs(5);
            line-height: gr(2);
            font-weight: 400;
            color: var(--base-700);


          }
        }

        :deep(.m-content) {
          font-size: fs(0);
          line-height: gr(1);
          color: var(--base-600);

          p {
            margin: 0;
          }

          strong {
            font-weight: 400;
            color: var(--blue-900);
          }
        }
      }
    }
  }

  .sec-blocks {}

  &.right {
    .sec-service-section {

      .row-header {
        flex-direction: row-reverse;
      }

      .row-content {
        flex-direction: row-reverse;
      }
    }
  }
}


.sphere-service-title {
  :deep(.a-title-primary) {
    font-size: fs(4);
    font-weight: 300;
    color: #466294;

    line-height: gr(2);
  }
}

.sec-sphere-content {
  .sphere-content-title {
    .a-title-primary {
      font-size: fs(5);
      font-weight: 400;
      color: var(--base-700);
      line-height: gr(2.25);
    }
  }
}
</style>
